/**
 * Pipeline API client (SPEC E3 — pipeline-and-capi, R2).
 *
 * Talks to the R1 endpoints (GET /api/pipeline/summary, /definitions, /list,
 * POST /api/pipeline/move) and the E0 opportunity endpoints (GET
 * /api/opportunities, PUT /api/opportunities/{id}). Follows the existing
 * fetch + Firebase-ID-token auth pattern from lib/api/prospects.ts.
 */

import { auth } from '../firebase';
import type {
  PipelineSummary,
  PipelineDefinition,
  Opportunity,
  OpportunityListResponse,
  StageMoveResponse,
} from '@/types/pipeline';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handle<T>(response: Response, what: string): Promise<T> {
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Failed to ${what}: ${response.statusText}`);
  }
  return response.json();
}

/** GET /api/pipeline/summary — per-stage count / total / weighted / stale. */
export async function getPipelineSummary(
  pipelineId?: string
): Promise<PipelineSummary> {
  const headers = await getAuthHeader();
  const qs = pipelineId ? `?pipeline_id=${encodeURIComponent(pipelineId)}` : '';
  const response = await fetch(`${API_BASE_URL}/api/pipeline/summary${qs}`, {
    headers,
  });
  return handle<PipelineSummary>(response, 'load pipeline summary');
}

/** GET /api/pipeline/definitions — the resolved pipeline definition (columns). */
export async function getPipelineDefinition(
  pipelineId?: string
): Promise<PipelineDefinition> {
  const headers = await getAuthHeader();
  const qs = pipelineId ? `?pipeline_id=${encodeURIComponent(pipelineId)}` : '';
  const response = await fetch(`${API_BASE_URL}/api/pipeline/definitions${qs}`, {
    headers,
  });
  const data = await handle<{ pipeline: PipelineDefinition }>(
    response,
    'load pipeline definition'
  );
  return data.pipeline;
}

/** GET /api/pipeline/list — the tenant's pipelines (the selector source). */
export async function listPipelines(): Promise<PipelineDefinition[]> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/pipeline/list`, { headers });
  const data = await handle<{ pipelines: PipelineDefinition[] }>(
    response,
    'list pipelines'
  );
  return data.pipelines;
}

/** GET /api/opportunities?stage_key=&pipeline_id= — deals for one column. */
export async function listOpportunities(params: {
  stageKey?: string;
  pipelineId?: string;
  limit?: number;
  offset?: number;
}): Promise<OpportunityListResponse> {
  const headers = await getAuthHeader();
  const qs = new URLSearchParams();
  if (params.stageKey) qs.append('stage_key', params.stageKey);
  if (params.pipelineId) qs.append('pipeline_id', params.pipelineId);
  if (params.limit !== undefined) qs.append('limit', String(params.limit));
  if (params.offset !== undefined) qs.append('offset', String(params.offset));
  const url = `${API_BASE_URL}/api/opportunities${
    qs.toString() ? `?${qs}` : ''
  }`;
  const response = await fetch(url, { headers });
  return handle<OpportunityListResponse>(response, 'list opportunities');
}

/** POST /api/pipeline/move — batch stage-move (drag-drop). */
export async function moveOpportunities(params: {
  opportunityIds: string[];
  stageKey: string;
  pipelineId?: string;
}): Promise<StageMoveResponse> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/pipeline/move`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      opportunity_ids: params.opportunityIds,
      stage_key: params.stageKey,
      pipeline_id: params.pipelineId,
    }),
  });
  return handle<StageMoveResponse>(response, 'move opportunities');
}

/** PUT /api/opportunities/{id} — partial update from the deal-detail panel. */
export async function updateOpportunity(
  opportunityId: string,
  updates: Partial<Opportunity>
): Promise<Opportunity> {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${API_BASE_URL}/api/opportunities/${opportunityId}`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    }
  );
  return handle<Opportunity>(response, 'update opportunity');
}
